/**
 * THE ANSWER VIEW-MODEL — provenance, formatting, chart choice and the one
 * navigation allowlist. Pure; no React, no DOM, no `@/` import.
 *
 * ══ "SHOW THE WORK" IS A DATA STRUCTURE HERE, NOT A DESIGN INTENTION ═══════
 *
 * Three fields ride on every answer because this estate has been burned by
 * their absence:
 *
 *   population_predicate  — WHO was counted. Two metrics that both say
 *                           "members" and disagree by 28 are not a bug; they
 *                           are two populations, and only this string says so.
 *   as_of / freshness_ok  — a pipeline once logged "completed" while
 *                           persisting nothing for 264 days. A watermark that
 *                           is visible on the answer is the cheapest instrument
 *                           against the repeat.
 *   environment           — the analytics reader defaults to the DEV project.
 *                           A PROD question answered with DEV data is the
 *                           quietest wrong answer available, so the envelope's
 *                           own environment is shown, and a MISMATCH against
 *                           the build's `VITE_ENV` is a warning pill.
 *
 * ══ AND ONE RULE ABOUT NAVIGATION ═════════════════════════════════════════
 *
 * 🔴 `safeRoute` IS AN EXACT-MATCH ALLOWLIST, NOT A SANITISER. The chat renders
 * assistant prose as plain text and every number from the structured result,
 * but a metric's `deep_link.href` is still a string arriving from a data
 * structure — and the EchoLeak / GrafanaGhost class of incident is exactly a
 * string that reached a navigable element, TWICE past a URL allowlist that
 * tried to parse rather than to enumerate. So this function enumerates. A
 * value not on the list is not "cleaned up"; it renders as text.
 */

import type {
  Cell,
  ColumnFormat,
  ColumnSpec,
  MetricResultEnvelope,
  Row,
} from './protocol';
import { chartValue, readCell } from './protocol';

// ═══════════════════════════════════════════════════════════════════════════
// NAVIGATION — the closed set of routes an agent surface may link to
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Every route a catalogue `deep_link.href` is allowed to name, with the LABEL
 * the catalogue declares for it.
 *
 * Checked from THREE sides by `tests/unit/lint/agent-metric-deeplinks-exist.test.ts`:
 * every key here is a real `<Route path>` in `App.tsx`; every `deep_link.href`
 * in the backend catalogue is a key here; and every label matches the one the
 * catalogue declares. A dead deep link is worse than no deep link, because it
 * teaches the operator that the citations are decorative — and a link whose
 * LABEL has drifted from the catalogue is the quieter version of the same
 * lesson.
 */
export const AGENT_DEEP_LINKS: Readonly<Record<string, string>> = {
  '/acquisition': 'Acquisition',
  // agent.coverage_weekly (2026-09-23): the agent reporting on itself.
  '/agent': 'Analytics agent',
  '/dashboard': 'Dashboard',
  '/feedback': 'Feedback',
  '/growth': 'Growth',
  '/launch-command-center': 'Launch command centre',
  '/matches': 'Matches',
  '/moderation': 'Photo moderation',
  // members.profile_answers (2026-09-22): the profile question catalogue.
  '/questions': 'Questions',
  '/reports': 'Reports',
  '/security/permissions': 'Permissions',
  '/system-health': 'System health',
};

export const AGENT_DEEP_LINK_ROUTES: readonly string[] = Object.keys(AGENT_DEEP_LINKS);

/** The exact-match allowlist. Returns the path, or `null` for "render as text". */
export function safeRoute(href: unknown): string | null {
  if (typeof href !== 'string') return null;
  return Object.prototype.hasOwnProperty.call(AGENT_DEEP_LINKS, href) ? href : null;
}

/**
 * The `{ href, label }` a metric's `definition_url` resolves to.
 *
 * `definition_url` IS `deep_link.href` (`disclosure/index.ts`), so the envelope
 * already carries the citation and the client needs no catalogue of its own —
 * which matters, because there ISN'T one in `frontend/admin/src` and inventing
 * one would be a second place for a metric's identity to drift.
 */
export function deepLinkFor(definitionUrl: unknown): { href: string; label: string } | null {
  const route = safeRoute(definitionUrl);
  return route === null ? null : { href: route, label: AGENT_DEEP_LINKS[route] };
}

// ═══════════════════════════════════════════════════════════════════════════
// PROVENANCE — the header pills and the two banners
// ═══════════════════════════════════════════════════════════════════════════

export interface AnswerProvenance {
  environment: string;
  /** True when the answer's environment differs from the build's own. */
  environmentMismatch: boolean;
  asOf: string | null;
  stale: boolean;
  populationPredicate: string | null;
  populationId: string | null;
  kApplied: number | null;
  tier: number | null;
  cellsSuppressed: number;
  cellsTotal: number;
  metricIds: string[];
}

/**
 * 🔴 COUNTED FROM THE RESULT, NOT FROM THE ROWS THAT WERE EMPTIED — the same
 * rule as `cellsTotal` in backend/src/agent/disclosure/index.ts. A result
 * withheld from the model in full has `rows: []` in the model view and keeps
 * its true row count in `derived.rows_total`. Reading `rows.length` alone put
 * "32 of 13 cells hidden" on a DEV card (2026-09-25): the hidden count took the
 * 25 cells of that result and the total took none of them.
 */
function cellCount(envelope: MetricResultEnvelope): number {
  const measures = envelope.columns.filter(
    (c) => c.role === 'measure' || c.role === 'derived',
  ).length;
  const rowsTotal = envelope.derived?.rows_total;
  const rows = typeof rowsTotal === 'number' && rowsTotal > envelope.rows.length
    ? rowsTotal
    : envelope.rows.length;
  return rows * measures;
}

/**
 * Fold every envelope a turn produced into ONE provenance block.
 *
 * The rules are deliberately pessimistic: the OLDEST watermark wins, any stale
 * envelope makes the answer stale, and the suppression counts SUM. An answer
 * assembled from three metrics is only as fresh as its stalest input, and
 * reporting the freshest one would be the reassuring lie.
 */
export function foldProvenance(
  envelopes: readonly MetricResultEnvelope[],
  buildEnvironment: string | undefined,
): AnswerProvenance | null {
  if (envelopes.length === 0) return null;

  let asOf: string | null = null;
  let stale = false;
  let cellsSuppressed = 0;
  let cellsTotal = 0;
  let kApplied: number | null = null;
  let tier: number | null = null;

  for (const envelope of envelopes) {
    if (asOf === null || (typeof envelope.as_of === 'string' && envelope.as_of < asOf)) {
      asOf = envelope.as_of ?? asOf;
    }
    if (envelope.freshness_ok === false) stale = true;
    cellsSuppressed += envelope.cells_suppressed ?? 0;
    cellsTotal += cellCount(envelope);
    if (typeof envelope.k_applied === 'number') {
      kApplied = kApplied === null ? envelope.k_applied : Math.max(kApplied, envelope.k_applied);
    }
    if (typeof envelope.tier === 'number') {
      tier = tier === null ? envelope.tier : Math.max(tier, envelope.tier);
    }
  }

  const first = envelopes[0];
  const environment = first.environment;
  return {
    environment,
    // An UNSET build variable is not a mismatch. Warning on it would make the
    // pill permanently amber in local development and train the eye to ignore
    // exactly the case it exists to catch.
    environmentMismatch:
      typeof buildEnvironment === 'string' &&
      buildEnvironment.length > 0 &&
      buildEnvironment !== environment,
    asOf,
    stale,
    populationPredicate: first.population_predicate ?? null,
    populationId: first.population_id ?? null,
    kApplied,
    tier,
    cellsSuppressed,
    cellsTotal,
    metricIds: [...new Set(envelopes.map((e) => e.metric_id))],
  };
}

/**
 * THE NON-PRODUCTION NAT BANNER (spec 14.7 mechanism 2).
 *
 * 🔴 THE EGRESS PROFILE IS READ FROM THE ENVELOPE, NEVER WRITTEN AS A LITERAL.
 * CDK derives it from `config.vpcConfig.useNatInstance` and stamps it into
 * every turn; a hardcoded string here would keep claiming "t4g.nano" after the
 * NAT is upgraded, which is a banner that lies about the very thing it exists
 * to explain. `agent-turn-state`'s suite passes a DIFFERENT profile value and
 * asserts it appears in the text, so a literal fails.
 *
 * Returns `null` on prod — a banner about non-production latency has no
 * business on the production screen.
 */
export function natBanner(input: {
  environment: string;
  egressProfile: string;
}): { egressProfile: string; message: string } | null {
  if (input.environment === 'prod') return null;
  const profile = input.egressProfile && input.egressProfile.trim().length > 0
    ? input.egressProfile.trim()
    : 'unknown';
  return {
    egressProfile: profile,
    message:
      `This environment egresses through “${profile}”; Anthropic round-trips here are ` +
      'measured above 25 seconds. Slow turns here are environment latency, not agent latency.',
  };
}

/**
 * The suppression notice, IN WORDS (spec 16.1).
 *
 * Returns `null` when nothing was suppressed — an always-present notice reading
 * "0 cells hidden" trains the eye past it, and the em-dash cells themselves are
 * the primary signal.
 */
/**
 * The standing banner over the FULL-PRECISION view.
 *
 * It used to say, on every answer, that these numbers "were not shown to the
 * model". Since the owner's decision of 2026-09-26 (the model receives the
 * operator's table), that sentence is false on most answers, so it is chosen
 * from the model's own envelopes: a withheld cell or a withheld-from-model
 * marker anywhere in the turn keeps the old warning; otherwise the banner says
 * the model received the same figures. Labels may still differ (an
 * off-vocabulary campaign name reaches the model as a token), so it claims
 * FIGURES, never labels.
 *
 * 🔴 NO FIGURES, NO BANNER (PROD 2026-09-28). A turn with no result - a
 * refusal, a "no data on that" answer - rendered "The model received these
 * same figures" over nothing, on 4 of 15 answers in the owner-question battery.
 * `null` means the card shows neither the banner nor the view toggle.
 */
export function precisionBanner(modelEnvelopes: readonly MetricResultEnvelope[]): {
  tone: 'warn' | 'neutral';
  text: string;
} | null {
  if (modelEnvelopes.length === 0) return null;
  const heldBack = modelEnvelopes.some(
    (envelope) => (envelope.cells_suppressed ?? 0) > 0 || envelope.withheld_from_model !== undefined,
  );
  return heldBack
    ? {
        tone: 'warn',
        text: 'Full precision. Some of these numbers were not shown to the model; the text below was written from the view it received.',
      }
    : { tone: 'neutral', text: 'Full precision. The model received these same figures.' };
}

export function suppressionNotice(provenance: AnswerProvenance): string | null {
  if (provenance.cellsSuppressed <= 0) return null;
  const total = provenance.cellsTotal > 0 ? provenance.cellsTotal : provenance.cellsSuppressed;
  const floor = provenance.kApplied;
  const tier = provenance.tier;
  const tierPart =
    floor === null
      ? 'A floor was applied to this answer.'
      : `Tier ${tier ?? '?'} floor: a cell must cover at least ${floor} members.`;
  return `${provenance.cellsSuppressed} of ${total} cells hidden. ${tierPart}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// FORMATTING — and the one path a withheld value may NOT take
// ═══════════════════════════════════════════════════════════════════════════

export type FormattedCell =
  | { kind: 'value'; text: string }
  | { kind: 'suppressed'; floor: number; text: '—'; label: string }
  | { kind: 'gap'; text: '—'; label: string };

const INTEGER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
/**
 * 🔴 THE FORMAT FOR A NUMBER WHOSE FORMAT IS UNKNOWN. It groups thousands and
 * otherwise changes NOTHING: it never rounds and never pads.
 *
 * `describeScalar` used to hardcode `'integer'` for every untyped number, and
 * `INTEGER` is `maximumFractionDigits: 0`. So an attribution credit of 2.4 and
 * one of 1.6 both printed `2` on the compare block, the SHARE page and the
 * pinned dashboard tile. `DECIMAL` is not the fix either — it sets
 * `minimumFractionDigits: 1`, which would print a count of 42 as `42.0`.
 *
 * Rounding a number the caller could not describe is a lie the operator cannot
 * see. Showing it exactly is the only honest default.
 */
const EXACT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 20 });
const DECIMAL = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 2,
});
const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${INTEGER.format(seconds)} s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} m ${Math.round(seconds % 60)} s`;
  return `${Math.floor(seconds / 3600)} h ${Math.round((seconds % 3600) / 60)} m`;
}

export function formatMeasure(value: number, format: ColumnFormat): string {
  switch (format) {
    case 'decimal':
      return DECIMAL.format(value);
    case 'percent':
      return `${DECIMAL.format(value)}%`;
    case 'currency_cents':
      return CURRENCY.format(value / 100);
    case 'seconds':
      return formatSeconds(value);
    case 'date':
    case 'text':
      return String(value);
    default:
      return INTEGER.format(value);
  }
}

/**
 * 🔴 THE ONE FUNCTION THAT MAY TURN A CELL INTO SOMETHING AN OPERATOR READS.
 *
 * There is no branch from `suppressed` or from `gap` into `formatMeasure`, and
 * that is the whole point: `Intl.NumberFormat().format(null)` is the string
 * "0", so a shared "format anything" helper would print a fabricated zero over
 * a number the disclosure engine declined to serve — worse than a blank,
 * because the reader cannot tell it from a measured zero (spec 4.5, 16.1).
 */
export function formatCell(cell: Cell | undefined, format: ColumnFormat): FormattedCell {
  const read = readCell(cell);
  switch (read.kind) {
    case 'number':
      return { kind: 'value', text: formatMeasure(read.value, format) };
    case 'text':
      return { kind: 'value', text: read.value };
    case 'suppressed':
      return {
        kind: 'suppressed',
        floor: read.floor,
        text: '—',
        label: `Withheld to protect a small group (disclosure floor ${read.floor})`,
      };
    default:
      return { kind: 'gap', text: '—', label: 'No data' };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// CHART CHOICE — from the ENVELOPE, never from the model (spec 16.2)
// ═══════════════════════════════════════════════════════════════════════════

export interface ChartPlan {
  kind: 'line' | 'bar';
  xKey: string;
  xLabel: string;
  series: { key: string; label: string; format: ColumnFormat }[];
  points: Record<string, string | number | null>[];
  /**
   * Set when a TWO-key result was pivoted: one series per value of the second
   * key. The chart then draws a legend (which line is which channel) and the
   * caption names the measure and the key the series split by.
   */
  pivot?: { measureLabel: string; seriesLabel: string };
}

/** A pivoted line chart stays readable up to six series; a seventh is a table. */
const MAX_PIVOT_LINES = 6;

const MAX_BAR_CATEGORIES = 14;
/** Grouped bars stay readable up to three series; a fourth is a table. A
 *  whole-window channel breakdown carries three measures, and one bar per
 *  measure per channel IS its natural picture (2026-09-15). */
const MAX_BAR_SERIES = 3;

/**
 * The tool results the answer card renders. The catalogue lookup, the metric
 * definition, the decline acknowledgement and a refused SQL attempt (the model
 * reads the reason and retries) are how the agent FOUND the answer, not the answer — the owner, on PROD, 2026-09-15: "this metrics found
 * block is annoying and useless for the user". Pinned by
 * tests/unit/admin/agent-answer-card-is-the-answer.test.ts.
 */
const NOT_THE_ANSWER: ReadonlySet<string> = new Set(['catalog', 'describe', 'acknowledged', 'attempt']);
/**
 * WHAT AN EMPTY TABLE MEANS — two different facts, and only one of them is
 * "there is no data".
 *
 * 🔴 When the disclosure engine withholds a result IN FULL it serves the model
 * `rows: []` (disclosure/index.ts, "EVERY row marked"). The table then printed
 * "The query ran and matched no rows … and it is not a zero" — a claim that no
 * data exists, on a card whose own footer said "6 cells hidden" and whose Full
 * precision view showed the row. Seen at the UI on DEV, 2026-09-20.
 *
 * The envelope already says which case it is: `derived.rows_total` counts the
 * groups the query produced, and it crosses to the model on purpose ("24 weeks,
 * all suppressed"). Zero groups is an empty result; more than zero with no rows
 * served is a withheld one. Returns `null` when the table has rows to show.
 */
export function emptyResultMessage(envelope: {
  rows: readonly unknown[];
  derived?: Readonly<Record<string, number>>;
  recorded_since?: RecordedSinceView;
  unavailable?: { reason: string; source_key: string };
  withheld_from_model?: {
    reason: string;
    floor_grain?: string;
    floor_window_days?: number;
    already_served?: string;
    floor?: number;
    ceiling?: number;
  };
}): { kind: 'withheld' | 'empty' | 'not_recorded' | 'not_read'; text: string } | null {
  if (envelope.rows.length > 0) return null;
  // 🔴 A SOURCE THAT WAS NOT READ (2026-10-09, issue #3385). The note above the table already says
  // "No number is served for this metric: <reason>". The table then said "For a count, that
  // means there are none" — under an ad network that was never connected, "we spent nothing",
  // which nobody measured. Nothing was read, so nothing can be none.
  if (envelope.unavailable) {
    return { kind: 'not_read', text: NOT_READ_TEXT };
  }
  // A period before the source began recording matched nothing because nothing
  // existed to match: "that means nothing was recorded in it" would read as a
  // zero. The recording fact replaces it (2026-09-28).
  if (envelope.recorded_since?.covers === 'nothing') {
    return { kind: 'not_recorded', text: recordedSinceText(envelope) ?? EMPTY_RESULT_TEXT };
  }
  // Below the processor's grain/window floor the model is sent NOTHING — not
  // even a group count — so `derived` is empty here and cannot be the signal.
  if (envelope.withheld_from_model?.reason === 'total_and_breakdown_within_24h') {
    return {
      kind: 'withheld',
      text:
        'Nothing from this result was sent to the AI model. In the last 24 hours it was already ' +
        `given this population ${envelope.withheld_from_model.already_served ?? 'in another view'}, and a ` +
        'total and its breakdown may not both be sent to the provider. That is a rule about what ' +
        'leaves for the provider, not about what you may see — “Full precision” shows you the ' +
        'whole table.',
    };
  }
  if (envelope.withheld_from_model?.reason === 'special_category_attribute') {
    return {
      kind: 'withheld',
      text:
        'Nothing from this result was sent to the AI model. This question is a special category ' +
        'of personal data (religion, ethnicity, political views or health habits), and counts of ' +
        'it are never sent to the provider. That is a rule about what leaves for the provider, ' +
        'not about what you may see — “Full precision” shows you the whole table.',
    };
  }
  if (envelope.withheld_from_model?.reason === 'population_below_floor') {
    return {
      kind: 'withheld',
      text:
        'Nothing from this result was sent to the AI model. The members it covers are fewer than ' +
        `${envelope.withheld_from_model.floor ?? 'the floor'}, the smallest population a composed ` +
        'question may send to the provider. That is a rule about what leaves for the provider, not ' +
        'about what you may see — “Full precision” shows you the whole table.',
    };
  }
  if (envelope.withheld_from_model?.reason === 'daily_budget_exhausted') {
    return {
      kind: 'withheld',
      text:
        'Nothing from this result was sent to the AI model. Today’s exploration budget for your ' +
        'account is spent, so composed results reach you and not the provider until tomorrow. ' +
        '“Full precision” shows you the whole table.',
    };
  }
  if (envelope.withheld_from_model?.reason === 'cannot_be_replayed') {
    return {
      kind: 'withheld',
      text:
        'Nothing from this result was sent to the AI model: a different view of the same members ' +
        'was already sent to the provider in the last 24 hours. “Full precision” shows you the ' +
        'whole table.',
    };
  }
  if (envelope.withheld_from_model) {
    const floor = envelope.withheld_from_model;
    return {
      kind: 'withheld',
      text:
        'Nothing from this result was sent to the AI model. Figures this fine-grained are not ' +
        `sent to the provider (its floor here is ${floor.floor_grain} grain over at least ` +
        `${floor.floor_window_days} days). That is a rule about what leaves for the provider, not ` +
        'about what you may see — “Full precision” shows you the whole table.',
    };
  }
  const total = envelope.derived?.rows_total;
  if (typeof total === 'number' && total > 0) {
    const rows = total === 1 ? 'The 1 row' : `All ${total} rows`;
    return {
      kind: 'withheld',
      text:
        `${rows} of this result ${total === 1 ? 'was' : 'were'} withheld from the model to protect a ` +
        'small group. That is not a zero and it is not missing data — “Full precision” shows ' +
        'you the figures.',
    };
  }
  return { kind: 'empty', text: EMPTY_RESULT_TEXT };
}

/** The envelope's `recorded_since` (protocol.ts `MetricResultEnvelope`). */
export interface RecordedSinceView {
  first_recorded_day: string | null;
  covers: 'nothing' | 'part';
  /** Per-network ad sources (migration 366): the last covered day, and the days of the period
   *  that no reading covered (after that day, or in a gap). */
  last_recorded_day?: string;
  not_recorded?: readonly { from: string; to: string }[];
  /** The first recorded day is where the SOURCE begins (relations.ts `sources_complete_from_first_day`). */
  source_begins?: true;
}

/**
 * WHERE THE SOURCE'S RECORDS BEGIN, as the operator reads it (2026-09-28).
 *
 * The model is told the same fact in its own words (`recordedSinceNote`,
 * tool-registry.ts); this is the card's. PROD's email events begin on
 * 2026-09-23: without this line an August table of email outcomes read as a
 * month in which nothing bounced. `null` when the result carries no fact.
 */
export function recordedSinceText(envelope: { recorded_since?: RecordedSinceView }): string | null {
  const fact = envelope.recorded_since;
  if (!fact) return null;
  if (fact.first_recorded_day === null) {
    return 'This source has not recorded anything yet, so no period has a figure.';
  }
  // 🔴 THE OTHER END (migration 366, issue #3385). An ad network is read a day or two late, so
  // "today" has no row: the table said "For a count, that means there are none" — a zero that
  // nobody measured. A gap between readings is the same in the middle of a period.
  if (fact.last_recorded_day && fact.not_recorded && fact.not_recorded.length > 0) {
    const days = fact.not_recorded.map((r) => (r.from === r.to ? r.from : `${r.from} to ${r.to}`)).join(', ');
    const range = `Recorded from ${fact.first_recorded_day} to ${fact.last_recorded_day}. Not recorded: ${days}.`;
    return fact.covers === 'nothing'
      ? `${range} Nothing in this period was recorded, so it has no figure. It is not a zero.`
      : `${range} These figures leave out the days that were not recorded.`;
  }
  // A source that BEGINS on its first recorded day (Google Play's export, 2026-10-11) has nothing
  // before it: "nothing was recorded" would suggest a gap in our records.
  if (fact.source_begins && fact.covers === 'nothing') {
    return `This source begins on ${fact.first_recorded_day}: it has nothing before that day, so this period has no figure.`;
  }
  return fact.covers === 'nothing'
    ? `Recorded since ${fact.first_recorded_day}. This period is before that day: nothing was recorded in it, so it has no figure.`
    : `Recorded since ${fact.first_recorded_day}. These figures cover ${fact.first_recorded_day} onward, not the whole period asked.`;
}

/**
 * 🔴 WHAT THE CARD SAYS WHEN THE SOURCE REPORTS A PROBLEM — TRUE WITH ROWS AND WITHOUT
 *    (2026-10-02, #2964 finding c5).
 *
 * The engine sets `unavailable` from the source's NEWEST collection (freshness.ts
 * `sourceWatermark`: the latest vendor_snapshot row failed, or its vendor_as_of is
 * older than `max_staleness_days`), and it keeps the rows of the earlier collections.
 * Every snapshot metric is a DAILY series (grouped by grain_date, grain `day` only)
 * over the collector's own columns (readings written, series returned, requests that
 * failed), so each row is what that day's collection recorded — a failed day too.
 * The card used to print "No number is served for this metric" in every case, above
 * a table that served the numbers. With no figure in the rows the old sentence is
 * true and stays.
 */
export function unavailableText(envelope: {
  rows: readonly Record<string, unknown>[];
  unavailable?: { reason: string; source_key: string };
}): { text: string; hasFigures: boolean } | null {
  const u = envelope.unavailable;
  if (!u) return null;
  const hasFigures = envelope.rows.some((row) =>
    Object.values(row).some((value) => typeof value === 'number' && Number.isFinite(value)),
  );
  return hasFigures
    ? {
        hasFigures,
        text:
          `The newest collection of this source reports a problem: ${u.reason} (${u.source_key}). ` +
          'The figures below are what each collection recorded, on the days shown.',
      }
    : { hasFigures, text: `No number is served for this metric: ${u.reason} (${u.source_key}).` };
}

/**
 * What an empty table says when the query truly matched nothing.
 *
 * 🔴 It used to end "and it is not a zero". Seen at the UI on DEV, 2026-09-21:
 * the owner's question "how many sign ups per day in the last 3 days?" had no
 * signups on those days, and the card told him the empty table "is not a
 * zero" — when none is exactly what happened. "Not a zero" is right for a
 * WITHHELD result (a figure exists and is hidden) and stays there. For a query
 * that matched nothing, the honest reading of a count is that there are none;
 * the sentence is conditional because a rate with no rows is not 0.
 *
 * 🔴 It names NO period (2026-09-29). PREDEV "what religions do our members
 * have" (a point-in-time snapshot, no answers yet) read "matched no rows in this
 * period … nothing was recorded in it": a snapshot has no period. The card
 * cannot tell the two apart (`EnvelopeBlock` gets the envelope, not the
 * invocation's grain), so the sentence is true of both: the model's own answer
 * names the period when there is one. A period before a source began recording
 * has its own sentence (`recordedSinceText`), never this one.
 */
export const EMPTY_RESULT_TEXT =
  'The query ran and matched no rows. For a count, that means there are none. ' +
  'That is an answer, not a failure.';

/** What an empty table says when its source was not read (`unavailable`, no row). */
export const NOT_READ_TEXT =
  'No figure: the source was not read for this period (the reason is above). This is not a ' +
  'zero — nothing was measured.';

/**
 * The heading of a column on the PINNED TILE and the SHARE PAGE.
 *
 * Those surfaces render a stored body through `RawTable`, which knows only the
 * row keys. The chat card heads the same numbers with the metric's labels, so
 * without this the two disagreed about what a column IS: "ITEMS 309" on the
 * tile, "Items ever written, every status 309" on the card (PROD, 2026-09-21).
 */
export function rawTableHeading(
  key: string,
  columns?: readonly { key: string; label?: string }[],
): string {
  const label = columns?.find((c) => c.key === key)?.label;
  return typeof label === 'string' && label.trim().length > 0 ? label : key.replace(/_/g, ' ');
}

export function rendersInAnswer(kind: string): boolean {
  return !NOT_THE_ANSWER.has(kind);
}
const MIN_CHART_ROWS = 2;

/**
 * Pick a chart form, or `null` for "the table is the answer".
 *
 * The model never gets a say. It cannot ask for a pie chart of a time series,
 * and it cannot make a suppressed cell into a plotted point, because the only
 * value path is `chartValue`, which maps every non-number to `null`.
 */
export function chooseChart(envelope: MetricResultEnvelope): ChartPlan | null {
  const columns = envelope.columns ?? [];
  const rows = envelope.rows ?? [];
  if (rows.length < MIN_CHART_ROWS) return null;

  const numeric = columns.filter((c) => c.role === 'measure' || c.role === 'derived');
  if (numeric.length === 0) return null;
  // 🔴 WHICH COLUMNS ARE CHARTED (2026-09-24). A count and a percent on one axis
  // flatten the percent and leave the axis with no honest unit, so a chart is
  // counts OR rates, never both. Which one depends on what was ASKED:
  //   - a COMPOSED result (explore) carries a rate only because the question
  //     named it — its counts are the parts that came with it — so it charts
  //     the rates;
  //   - a CATALOGUE metric carries its rates always, whatever was asked, so it
  //     charts its counts as it did before rates existed. The first version
  //     charted rates whenever present, and at the UI on PREDEV "signups by
  //     channel each month" drew no chart at all (two rates, no single series),
  //     and every signups chart would have become a completion-rate chart.
  const composed = envelope.metric_id.startsWith('explore:');
  const rates = numeric.filter((c) => c.role === 'derived');
  const counts = numeric.filter((c) => c.role === 'measure');
  const measures = composed && rates.length > 0 ? rates : counts.length > 0 ? counts : rates;

  const time = columns.find((c) => c.role === 'time');
  const dimensions = columns.filter((c) => c.role === 'dimension');
  // TWO KEYS (2026-09-24): plotting every row on the first key drew several
  // points per week on one line. Pivot on the second key instead, or leave the
  // table as the answer.
  if ((time ? 1 : 0) + dimensions.length >= 2) {
    const axis = time ?? dimensions[0];
    const split = dimensions.find((d) => d.key !== axis.key);
    // One series per category needs ONE measure. A catalogue metric declares its
    // headline first (signups, matches, members attempted), so it pivots on
    // that; a composed result pivots only when one measure was charted.
    const pivotOn = measures.length === 1 || !composed ? measures[0] : undefined;
    if (!split || !pivotOn) return null;
    return pivotPlan(time ? 'line' : 'bar', axis, split, pivotOn, rows);
  }
  if (time) return plan('line', time, measures, rows);

  const dimension = columns.find((c) => c.role === 'dimension');
  if (dimension && rows.length <= MAX_BAR_CATEGORIES && measures.length <= MAX_BAR_SERIES) {
    return plan('bar', dimension, measures, rows);
  }
  return null;
}

function plan(
  kind: 'line' | 'bar',
  axis: ColumnSpec,
  measures: ColumnSpec[],
  rows: Row[],
): ChartPlan {
  return {
    kind,
    xKey: axis.key,
    xLabel: axis.label,
    series: measures.map((m) => ({ key: m.key, label: m.label, format: m.format })),
    points: rows.map((row) => {
      const point: Record<string, string | number | null> = {};
      const axisCell = readCell(row[axis.key]);
      point[axis.key] =
        axisCell.kind === 'number'
          ? axisCell.value
          : axisCell.kind === 'text'
            ? axisCell.value
            : '—';
      // A GAP, never a zero. Recharts breaks the line; a 0 would plot a point.
      for (const measure of measures) point[measure.key] = chartValue(row[measure.key]);
      return point;
    }),
  };
}

/** The text a label cell reads as on a chart axis or legend. */
function labelText(cell: Cell | undefined): string {
  const read = readCell(cell);
  if (read.kind === 'number') return String(read.value);
  if (read.kind === 'text') return read.value;
  return '—';
}

/**
 * One series per value of `split`, drawn over `axis`. PURE.
 *
 * A combination the result does not carry is a GAP (`null`), never 0: a group
 * with no row may be empty or may be withheld, and the chart must not claim
 * which. Series keys are positional (`s0`, `s1`, ...) so a label can never be
 * mistaken for a column name, and they follow the order values first appear
 * in, which is the order the engine returned the rows in.
 */
function pivotPlan(
  kind: 'line' | 'bar',
  axis: ColumnSpec,
  split: ColumnSpec,
  measure: ColumnSpec,
  rows: Row[],
): ChartPlan | null {
  const axisValues: string[] = [];
  const splitValues: string[] = [];
  for (const row of rows) {
    const a = labelText(row[axis.key]);
    const s = labelText(row[split.key]);
    if (!axisValues.includes(a)) axisValues.push(a);
    if (!splitValues.includes(s)) splitValues.push(s);
  }
  const maxSeries = kind === 'line' ? MAX_PIVOT_LINES : MAX_BAR_SERIES;
  if (splitValues.length > maxSeries) return null;
  if (kind === 'bar' && axisValues.length > MAX_BAR_CATEGORIES) return null;

  const seriesKey = (index: number) => `s${index}`;
  const points = axisValues.map((a) => {
    const point: Record<string, string | number | null> = { [axis.key]: a };
    splitValues.forEach((s, index) => {
      const row = rows.find((r) => labelText(r[axis.key]) === a && labelText(r[split.key]) === s);
      point[seriesKey(index)] = row ? chartValue(row[measure.key]) : null;
    });
    return point;
  });
  return {
    kind,
    xKey: axis.key,
    xLabel: axis.label,
    series: splitValues.map((s, index) => ({ key: seriesKey(index), label: s, format: measure.format })),
    points,
    pivot: { measureLabel: measure.label, seriesLabel: split.label },
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// TABLE SORT — in the browser, over the WHOLE result
// ═══════════════════════════════════════════════════════════════════════════

/**
 * The rows ordered by one column, for the table's clickable headers. PURE.
 *
 * Sorting in the browser is correct here and NOT on the list pages
 * (`components/table/SortableHeader` says why): an answer's table is the whole
 * result, never one page of it. What the MODEL received keeps the engine's
 * order, so a sort position can never rank a withheld cell for the model.
 *
 * Numbers sort as numbers, text as text; a withheld cell and a gap sort LAST in
 * both directions, so "highest first" never puts a hidden figure at the top.
 * Ties keep the engine's order (the sort is stable).
 */
export function sortRows(rows: readonly Row[], key: string | null, order: 'asc' | 'desc'): Row[] {
  if (key === null) return [...rows];
  const rank = (row: Row): { kind: 0 | 1 | 2; num: number; text: string } => {
    const read = readCell(row[key]);
    if (read.kind === 'number') return { kind: 0, num: read.value, text: '' };
    if (read.kind === 'text') return { kind: 1, num: 0, text: read.value };
    return { kind: 2, num: 0, text: '' };
  };
  const sign = order === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index, r: rank(row) }))
    .sort((x, y) => {
      if (x.r.kind === 2 || y.r.kind === 2) {
        if (x.r.kind !== y.r.kind) return x.r.kind === 2 ? 1 : -1;
        return x.index - y.index;
      }
      if (x.r.kind !== y.r.kind) return x.r.kind - y.r.kind;
      const by = x.r.kind === 0 ? x.r.num - y.r.num : x.r.text.localeCompare(y.r.text);
      return by !== 0 ? sign * by : x.index - y.index;
    })
    .map((x) => x.row);
}

/**
 * The union of keys across an untyped row list, for the delta table and the
 * `records` presentation — the two shapes that arrive without a `columns[]`.
 */
export function unionKeys(rows: readonly Record<string, unknown>[]): string[] {
  const keys: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) if (!keys.includes(key)) keys.push(key);
  }
  return keys;
}

/**
 * 🔴 THE COLUMN ORDER IS THE METRIC'S, NOT THE STORE'S (PREDEV, 2026-09-25).
 *
 * A turn result is kept as a DynamoDB map (transport/agent-turns-table.ts), and
 * a map does not keep key order. So a row's own key order is not the metric's:
 * the Difference table under "signups in August against July" read the two
 * rates first and Signups fourth, while the window tables above it, which follow
 * `columns`, read Signups first. The named columns come first, in their order;
 * any other key follows, so nothing a row carries is dropped. PURE.
 */
export function rawTableKeys(
  rows: readonly Record<string, unknown>[],
  columns?: readonly { key: string }[],
): string[] {
  const keys = unionKeys(rows);
  if (!columns || columns.length === 0) return keys;
  const named = columns.map((c) => c.key).filter((key) => keys.includes(key));
  return [...named, ...keys.filter((key) => !named.includes(key))];
}

/**
 * A scalar rendered as text, for the untyped shapes above.
 *
 * Objects and arrays are summarised rather than `JSON.stringify`-ed into the
 * cell: an untyped nested object in a table cell is unreadable, and stringifying
 * it is also how a label carrying markup ends up somewhere it was not reviewed.
 */
export function describeScalar(value: unknown, format?: ColumnFormat): FormattedCell {
  if (value === null || value === undefined) return { kind: 'gap', text: '—', label: 'No data' };
  // A KNOWN format wins. With none, EXACT — never `integer`, which rounds.
  if (typeof value === 'number') {
    return {
      kind: 'value',
      text: format === undefined ? EXACT.format(value) : formatMeasure(value, format),
    };
  }
  if (typeof value === 'string') return { kind: 'value', text: value };
  if (typeof value === 'boolean') return { kind: 'value', text: value ? 'yes' : 'no' };
  if (Array.isArray(value)) return { kind: 'value', text: `${value.length} item(s)` };
  const cellLike = formatCell(value as Cell, 'integer');
  return cellLike.kind === 'value' ? { kind: 'value', text: '(object)' } : cellLike;
}

/**
 * Why a Pin or Share button is greyed out.
 *
 * Save -> Pin -> Share is a chain: a pin is OF a saved question and a share
 * link is OF a pin. The later buttons are disabled until the earlier step is
 * done, and until 2026-09-21 nothing on the screen said so - a grey button
 * with no reason reads as a broken one. Returns `undefined` when the button is
 * usable or already done, so no tooltip claims a step is missing when it is not.
 */
export function lockedActionHint(
  action: 'pin' | 'share',
  done: { saved: boolean; pinned: boolean },
): string | undefined {
  if (action === 'pin') return done.saved ? undefined : 'Save the question first - a pin is of a saved question.';
  if (!done.saved) return 'Save the question, then pin it - a share link is of a pinned answer.';
  return done.pinned ? undefined : 'Pin the answer first - a share link is of a pinned answer.';
}
